import type { ReploSimpleProject } from "schemas/generated/project";
import type { CurrencyCode } from "schemas/project";
import type { ShopSettings } from "../../../../../features/shops/hooks/useUpdateShop";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { Loader } from "@editor/components/common/Loader";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useHasWorkspaceReachedCustomDomainLimit } from "@editor/hooks/useHasWorkspaceReachedCustomDomainLimit";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useOpenModal } from "@editor/hooks/useModal";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { shopMetadataRouter } from "@editor/trpc/shopMetadata";
import { getCurrencySymbol } from "@editor/utils/analytics";

import {
  LoadedSettings,
  SettingsContent,
  SettingsLayout,
} from "@/editor/components/shops-ui/settings/SettingsCard";
import { routes } from "@/editor/utils/router";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { formatInTimeZone } from "date-fns-tz";
import { Clock, Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { generatePath } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatIanaTimezone } from "replo-utils/lib/timezone";
import { ReploError } from "schemas/errors";
import { CURRENCIES } from "schemas/project";

import { useUpdateShop } from "../../../../../features/shops/hooks/useUpdateShop";
import { DomainsDisplay } from "./domains/DomainsDisplay";
import { PaymentMethodSection } from "./PaymentMethodSection";

class FaviconUploadError extends ReploError {}

const ShopSettingsForm: React.FC<{
  project: ReploSimpleProject;
}> = ({ project }) => {
  const { updateShop, isUpdating } = useUpdateShop();
  const {
    handleSubmit,
    setValue,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<ShopSettings>({
    defaultValues: {
      name: project.name ?? "",
      timeZone:
        project.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: project.currency ?? "USD",
    },
  });

  const handleSetValue = (
    key: keyof ShopSettings,
    value: ShopSettings[keyof ShopSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateSettingsSections = [
    {
      title: "Shop Name",
      component: (
        <ShopNameInput
          initialName={watch("name") ?? ""}
          onChange={(value) => handleSetValue("name", value)}
        />
      ),
    },
    {
      title: "Timezone",
      component: (
        <ShopTimeZoneSettings
          timeZone={watch("timeZone") ?? ""}
          onChange={(value) => handleSetValue("timeZone", value)}
        />
      ),
    },
    {
      title: "Currency",
      component: (
        <ShopCurrencySettings
          currency={watch("currency") ?? "USD"}
          onChange={(value) => handleSetValue("currency", value)}
        />
      ),
    },
  ];

  const handleShopUpdate = async (shopSettings: ShopSettings) => {
    await updateShop({
      id: project.id,
      data: shopSettings,
    });
    reset({}, { keepValues: true });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {updateSettingsSections.map((section) => {
        return (
          <SettingsContent key={section.title} title={section.title}>
            {section.component}
          </SettingsContent>
        );
      })}
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleShopUpdate)()}
          isLoading={isUpdating}
          disabled={!isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const ShopTimeZoneSettings: React.FC<{
  timeZone: string;
  onChange: (value: string) => void;
}> = ({ timeZone, onChange }) => {
  const getTimeZoneLabel = (timezone: string) => {
    if (timezone === "UTC") {
      return "UTC (GMT±00:00)";
    }

    const formattedTimezone = formatIanaTimezone(timezone);

    // NOTE (Kurt, 2025-03-03): We format the GMT part to be more readable by
    // replacing GMT+0:00 or GMT-0:00 with GMT±0:00.
    let gmtPart = formatInTimeZone(new Date(), timezone, "'GMT'xxx");
    gmtPart = gmtPart.replace(/GMT[+-]00:00/g, "GMT±00:00");

    return `${formattedTimezone} (${gmtPart})`;
  };

  const timezoneOptions = Intl.supportedValuesOf("timeZone").map(
    (timezone) => ({
      label: getTimeZoneLabel(timezone),
      value: timezone,
    }),
  );

  timezoneOptions.unshift({
    label: getTimeZoneLabel("UTC"),
    value: "UTC",
  });

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={timezoneOptions}
        value={timeZone ?? undefined}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={getTimeZoneLabel(timeZone ?? "")}
            titleAlignment="start"
            isPlaceholder={!Boolean(timeZone)}
            startEnhancer={<Clock size={14} />}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported using this timezone.
      </div>
    </div>
  );
};

const ShopCurrencySettings: React.FC<{
  currency: CurrencyCode;
  onChange: (value: string) => void;
}> = ({ currency, onChange }) => {
  const formatCurrencyLabel = (code: CurrencyCode, name: string) => {
    const symbol = getCurrencySymbol(code);
    // NOTE (Kurt, 2025-04-02): Some currency symbols aren't rendered consistently in the browser, so the
    // Intl API returns the currency code instead, if we get a code back we don't want to show it since we
    // already show the currency code in the label.
    return `${code} - ${name}${symbol !== code ? ` (${symbol})` : ""}`;
  };

  const currencyOptions = Object.entries(CURRENCIES).map(
    ([code, { name }]) => ({
      label: formatCurrencyLabel(code as CurrencyCode, name),
      value: code,
    }),
  );

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={currencyOptions}
        value={currency}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={formatCurrencyLabel(currency, CURRENCIES[currency].name)}
            titleAlignment="start"
            isPlaceholder={!Boolean(currency)}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported in this currency.
      </div>
    </div>
  );
};

const ShopNameInput = ({
  initialName,
  onChange,
}: {
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your shop name"
        size="base"
      />
    </div>
  );
};

type ShopSectionProps = {
  project: ReploSimpleProject;
};

const ShopMoveSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const openModal = useOpenModal();

  return (
    <div className="flex w-full flex-row justify-end">
      <Button
        variant="primary"
        size="base"
        disabled={disabled}
        onClick={() => {
          if (project.ownerWorkspace?.id) {
            openModal({
              type: "moveProjectModal",
              props: {
                projectId: project.id,
                oldWorkspaceId: project?.ownerWorkspace?.id,
                shopifyUrl: project.integrations?.shopify?.store.shopifyUrl,
              },
            });
          }
        }}
      >
        Move Shop
      </Button>
    </div>
  );
};

const ShopDeleteSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const openModal = useOpenModal();

  return (
    <div className="flex flex-col gap-4">
      <InlineAlert variant="error" multiline>
        Deleting this shop will erase it. You will not be able to recover this
        Shop without the help of the Replo support team.
      </InlineAlert>
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="danger"
          size="base"
          disabled={disabled}
          onClick={() => {
            openModal({
              type: "deleteProjectModal",
              props: {
                projectId: project.id,
                from: "list",
              },
            });
          }}
        >
          Delete Shop
        </Button>
      </div>
    </div>
  );
};

const ShopFaviconSection: React.FC<ShopSectionProps> = () => {
  const logEvent = useLogAnalytics();
  const [lightThemeIcon, setLightThemeIcon] = React.useState<string | null>(
    null,
  );
  const [darkThemeIcon, setDarkThemeIcon] = React.useState<string | null>(null);
  const [isLightUploadLoading, setIsLightUploadLoading] = React.useState(false);
  const [isDarkUploadLoading, setIsDarkUploadLoading] = React.useState(false);
  const lightFileInputRef = React.useRef<HTMLInputElement>(null);
  const darkFileInputRef = React.useRef<HTMLInputElement>(null);

  const { project } = useCurrentProjectContext();

  const { mutate: updateProject } = projectRouter.update.useMutation({
    onSuccess: () => {
      void projectUtils.getV2.invalidate();
    },
  });

  const { mutateAsync: generateSignedUrlForFavicon } =
    shopMetadataRouter.generateSignedUrlForFavicon.useMutation();

  React.useEffect(() => {
    return () => {
      if (lightThemeIcon) {
        URL.revokeObjectURL(lightThemeIcon);
      }
      if (darkThemeIcon) {
        URL.revokeObjectURL(darkThemeIcon);
      }
    };
  }, [lightThemeIcon, darkThemeIcon]);

  if (!project) {
    return null;
  }

  const handleFileUpload = async (
    file: File,
    setIcon: (url: string) => void,
    faviconStyle: "light-mode" | "dark-mode",
    setIsLoading: (loading: boolean) => void,
  ) => {
    const isValidFile =
      file.type.startsWith("image/") ||
      /\.(ico|png|gif|jpe?g)$/i.test(file.name);

    if (!isValidFile) {
      return;
    }

    try {
      setIsLoading(true);

      const objectUrl = URL.createObjectURL(file);
      setIcon(objectUrl);

      const { signedUrl, publicUrl } = await generateSignedUrlForFavicon({
        projectId: project.id,
        faviconStyle: faviconStyle,
        contentType: file.type,
        contentLength: file.size,
      });

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "Content-Length": file.size.toString(),
        },
      });

      if (!uploadResponse.ok) {
        throw new FaviconUploadError({
          message: `Failed to upload image ${file.name} to Cloudflare`,
          additionalData: { status: uploadResponse.status },
        });
      }

      updateProject({
        projectId: project.id,
        project: {
          ...(faviconStyle === "light-mode"
            ? { shopMetadataLightModeFaviconUrl: publicUrl }
            : { shopMetadataDarkModeFaviconUrl: publicUrl }),
        },
      });

      logEvent("site.settings.favicon.update", {
        faviconStyle,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createUploadHandler =
    (
      setIcon: (url: string) => void,
      faviconStyle: "light-mode" | "dark-mode",
      setIsLoading: (loading: boolean) => void,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void handleFileUpload(file, setIcon, faviconStyle, setIsLoading);
      }
    };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col gap-2 flex-1">
        <div className="relative">
          <img
            src="/images/shops/BrowserLight.png"
            alt="Light theme preview"
            className="w-full object-contain rounded-lg"
          />
          <img
            src={
              lightThemeIcon ||
              project.shopMetadataLightModeFaviconUrl ||
              "/images/shops/DarkLogo.png"
            }
            alt="Dark logo overlay"
            className="absolute object-contain"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(+165%, -165%)",
              width: "28px",
              height: "28px",
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="pl-2 font-semibold text-base">Light Theme</div>
          <Button
            variant="secondary"
            size="sm"
            layoutClassName="mr-2"
            isLoading={isLightUploadLoading}
            disabled={isLightUploadLoading}
            onClick={() => lightFileInputRef.current?.click()}
          >
            {project.shopMetadataLightModeFaviconUrl ? "Replace" : "Upload"}
          </Button>
          <input
            ref={lightFileInputRef}
            type="file"
            accept=".ico,.png,.gif,.jpg,.jpeg"
            onChange={createUploadHandler(
              setLightThemeIcon,
              "light-mode",
              setIsLightUploadLoading,
            )}
            style={{ display: "none" }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="relative">
          <img
            src="/images/shops/BrowserDark.png"
            alt="Dark theme preview"
            className="w-full object-contain rounded-lg"
          />
          <img
            src={
              darkThemeIcon ||
              project.shopMetadataDarkModeFaviconUrl ||
              "/images/shops/LightLogo.png"
            }
            alt="Light logo overlay"
            className="absolute object-contain"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(+165%, -165%)",
              width: "28px",
              height: "28px",
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="pl-2 font-semibold text-base">
            Dark Theme{" "}
            <span className="text-placeholder text-sm font-normal">
              Optional
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            layoutClassName="mr-2"
            isLoading={isDarkUploadLoading}
            disabled={isDarkUploadLoading}
            onClick={() => darkFileInputRef.current?.click()}
          >
            {project.shopMetadataDarkModeFaviconUrl ? "Replace" : "Upload"}
          </Button>
          <input
            ref={darkFileInputRef}
            type="file"
            accept=".ico,.png,.gif,.jpg,.jpeg"
            onChange={createUploadHandler(
              setDarkThemeIcon,
              "dark-mode",
              setIsDarkUploadLoading,
            )}
            style={{ display: "none" }}
          />
        </div>
      </div>
    </div>
  );
};

const ShopSettingsWrapper = () => {
  const { project, isLoading: isLoadingProject } = useCurrentProjectContext();

  const hasWorkspaceReachedCustomDomainLimit =
    useHasWorkspaceReachedCustomDomainLimit();

  const { userRole } = useCurrentProjectContext();
  const canDeleteOrMoveShop = exhaustiveSwitch({ type: userRole ?? "none" })({
    superuser: () => true,
    workspaceOwner: () => true,
    workspaceMember: () => false,
    projectMember: () => false,
    none: () => false,
  });

  if (!project) {
    return null;
  }

  const showReploHostedSections = project.hostingType === "replo";
  const showPaymentMethodSection = project.hostingType === "replo";

  const sections = [
    ...(showPaymentMethodSection
      ? [
          {
            title: "Commerce Platform",
            description: "How you manage products and get paid",
            component: <PaymentMethodSection project={project} />,
          },
        ]
      : []),
    {
      title: "General Shop Settings",
      component: <ShopSettingsForm project={project} />,
    },
    ...(showReploHostedSections
      ? [
          {
            title: "Domains",
            actions: (
              <div className="flex gap-2">
                {hasWorkspaceReachedCustomDomainLimit && (
                  <Button
                    variant="link"
                    size="base"
                    to={`/shops/${project.id}/settings/workspace/billing`}
                  >
                    Upgrade your plan
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="base"
                  startEnhancer={<Plus size={12} />}
                  onClick={() => {
                    // Navigate to dedicated add-domain page with OAuth-compatible headers
                    if (project.id) {
                      const addDomainPath = generatePath(
                        routes.shops.settings.shops.addDomain,
                        { projectId: project.id },
                      );
                      window.location.href = addDomainPath;
                    }
                  }}
                  disabled={hasWorkspaceReachedCustomDomainLimit}
                  tooltipText={
                    hasWorkspaceReachedCustomDomainLimit
                      ? "Upgrade to a paid plan to add a custom domain"
                      : undefined
                  }
                >
                  Add Domain
                </Button>
              </div>
            ),
            component: <DomainsDisplay project={project} />,
          },
        ]
      : []),
    ...(showReploHostedSections
      ? [
          {
            title: "Site Favicon",
            description:
              "Upload a 64x64 pixel ICO, PNG, GIF, or JPG to display in browser tabs.",
            component: <ShopFaviconSection project={project} />,
          },
        ]
      : []),
    ...(canDeleteOrMoveShop
      ? [
          {
            title: "Move Shop",
            description: "Move this shop to a different workspace.",
            component: <ShopMoveSection project={project} disabled={false} />,
          },
          {
            title: "Delete Shop",
            component: <ShopDeleteSection project={project} disabled={false} />,
          },
        ]
      : []),
  ];

  return (
    <SettingsLayout>
      {isLoadingProject ? <Loader /> : <LoadedSettings sections={sections} />}
    </SettingsLayout>
  );
};

export default ShopSettingsWrapper;
